// ContentEditableSurvey: fixes cursor jump by only updating content when value changes from outside
import { FC, useRef, useEffect, useState } from 'react';
import VisualSurveyEditor from '../Survey/VisualSurveyEditor';

interface ContentEditableSurveyProps {
    value: string;
    onChange: (v: string) => void;
    style: React.CSSProperties;
}
const ContentEditableSurvey: FC<ContentEditableSurveyProps> = ({ value, onChange, style }) => {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (ref.current && ref.current.innerText !== value) {
            ref.current.innerText = value;
        }
    }, [value]);
    return (
        <div
            ref={ref}
            contentEditable
            suppressContentEditableWarning
            spellCheck={false}
            style={style}
            onInput={e => {
                const text = (e.target as HTMLDivElement).innerText;
                onChange(text);
            }}
            aria-label="Survey JSON data editor"
        />
    );
};

import { Settings48Regular, BinRecycle24Regular, PersonDelete24Regular, WeatherSunny24Regular, WeatherMoon24Regular, ArrowDownload24Regular, SignOut24Regular, Lightbulb24Regular, Warning24Regular, Eye24Regular, EyeOff24Regular, ArrowUpload24Regular, Save24Regular, Info16Regular, Code24Regular, DesignIdeas24Regular } from '@fluentui/react-icons';
import { Title1, Text, Divider, tokens, Button, Switch, Dropdown, Option, Input, Textarea, Link, Tooltip } from '@fluentui/react-components';

// Types for supported languages and features
interface SupportedLanguage {
    locale: string;
    displayLanguage: string;
}

interface VoiceEntryType {
    key: string;
    text: string;
}

// Props interface for SettingsView
interface SettingsViewProps {
    classes: Record<string, string>;
    t: (key: string, defaultValue?: string) => string;
    theme: string;
    LIGHT_THEME: string;
    DARK_THEME: string;
    DisenrollFromPlatformAvailable: boolean;
    selectedLanguage: string;
    speechToTextSupportedLanguages: SupportedLanguage[];
    voiceEntryType: string;
    voiceEntryTypes: VoiceEntryType[];
    autoSendNumSecsInput: string;
    autoSendNumSecsError: string;
    handleThemeToggle: () => void;
    handleClearConversations: () => void;
    handleDisenroll: () => void;
    onUpdateUserPreferencesLanguage: (locale: string) => void;
    onUpdateVoiceEntryType: (entryType: string) => void;
    onUpdateAutoSendNumSecs: (numSecs: number) => void;
    validateAutoSendNumSecs: (value: string) => boolean;
    setAutoSendNumSecsInput: (value: string) => void;
    handleLogout: () => void;
    showLanguageSelectionInPrompt: boolean;
    onUpdateShowLanguageSelectionInPrompt: (show: boolean) => void;
    // Survey management props
    surveyManagementEnabled: boolean;
    onFileUpload: (file: File) => Promise<void>;
    isUploading: boolean;
    // Survey activation props
    isSurveyActive: boolean;
    onSurveyActivationChange: (isActive: boolean) => void;
    // Survey data display
    currentSurvey: any;
    isLoadingSurvey: boolean;
    // Survey editing props
    editedSurveyJson: string;
    setEditedSurveyJson: (value: string) => void;
    onSaveSurvey: () => Promise<void>;
    onSaveSurveyDirect?: (surveyJson: string) => Promise<void>;
    isSaving: boolean;
    // Visual editor props
    showVisualEditor?: boolean;
    onToggleEditor?: () => void;
}

const SettingsView: FC<SettingsViewProps> = (props) => {
    const MAX_AUTO_SEND_SECONDS = 5;
    const isMobile = window.innerWidth <= 768;
    const [localShowVisualEditor, setLocalShowVisualEditor] = useState(true);

    const {
        classes,
        t,
        theme,
        LIGHT_THEME,
        DARK_THEME,
        DisenrollFromPlatformAvailable,
        selectedLanguage,
        speechToTextSupportedLanguages,
        voiceEntryType,
        voiceEntryTypes,
        autoSendNumSecsInput,
        autoSendNumSecsError,
        handleThemeToggle,
        handleClearConversations,
        handleDisenroll,
        onUpdateUserPreferencesLanguage,
        onUpdateVoiceEntryType,
        onUpdateAutoSendNumSecs,
        validateAutoSendNumSecs,
        setAutoSendNumSecsInput,
        handleLogout,
        showLanguageSelectionInPrompt,
        onUpdateShowLanguageSelectionInPrompt,
        // Survey management props
        surveyManagementEnabled,
        onFileUpload,
        isUploading,
        // Survey activation props
        isSurveyActive,
        onSurveyActivationChange,
        // Survey data display
        currentSurvey,
        isLoadingSurvey,
        // Survey editing props
        editedSurveyJson,
        setEditedSurveyJson,
        onSaveSurvey,
        onSaveSurveyDirect,
        isSaving,
        // Visual editor props
        showVisualEditor,
        onToggleEditor,
    } = props;

    return (
        <div className={classes.container}>
            <div className={classes.titleContainer}>
                <Settings48Regular className={classes.settingsIcon} />
                <Title1 className={classes.settingsTitle}>{t('settings.title', 'Settings')}</Title1>
            </div>
            <div className={classes.settingsContent}>
                {/* Application Settings Section */}
                <div className={classes.settingsSection}>
                    <Text className={classes.sectionTitle} size={500}>
                        {t('settings.appearance.title', 'Appearance')}
                    </Text>

                    <div className={classes.themeSettingContainer} style={isMobile ? { flexDirection: 'column', alignItems: 'flex-start' } : {}}>
                        <div className={classes.themeSettingLeft}>
                            <Text size={400} weight="semibold">
                                {t('settings.theme.title', 'Theme')}
                            </Text>
                            <Text size={400} style={{ color: tokens.colorNeutralForeground2 }}>
                                {t('settings.theme.description', 'Choose between light and dark appearance')}
                            </Text>
                        </div>
                        <div className={classes.themeSettingRight} style={isMobile ? { width: '100%', justifyContent: 'left', marginTop: '12px' } : {}}>
                            <WeatherSunny24Regular className={theme === LIGHT_THEME ? classes.themeIconLight : classes.themeIconDark} />
                            <Switch
                                className={theme === DARK_THEME ? classes.themeSwitchDark : classes.themeSwitchLight}
                                checked={theme === DARK_THEME}
                                onChange={handleThemeToggle}
                                aria-label={t('settings.theme.toggle', 'Toggle theme')}
                            />
                            <WeatherMoon24Regular className={theme === DARK_THEME ? classes.themeIconDark : classes.themeIconLight} />
                        </div>
                    </div>



                    {/* Voice Type Settings */}
                    <div className={classes.voiceSettingContainer} style={isMobile ? { marginTop: '0px', paddingTop: '8px' } : {}}>
                        <Text className={classes.sectionTitle} size={500}>
                            {t('settings.voice.title', 'Voice Type Settings')}
                        </Text>

                        <div className={classes.voiceSettingRow}>
                            <div className={classes.voiceSettingLeft}>
                                <Text size={400} weight="semibold">
                                    {t('settings.voice.language', 'Language')}
                                </Text>
                                <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                                    {t('settings.voice.languageDescription', 'Select the language for voice recognition')}
                                </Text>
                            </div>
                            <div className={classes.voiceSettingRight}>
                                <Dropdown
                                    aria-labelledby="language-dropdown"
                                    placeholder="Select language"
                                    value={speechToTextSupportedLanguages.find((lang: SupportedLanguage) => lang.locale === selectedLanguage)?.displayLanguage}
                                    selectedOptions={[selectedLanguage]}
                                    onOptionSelect={(_, data) => {
                                        if (data.optionValue) {
                                            onUpdateUserPreferencesLanguage(data.optionValue);
                                        }
                                    }}
                                >
                                    {speechToTextSupportedLanguages.map((language: SupportedLanguage) => (
                                        <Option key={language.locale} value={language.locale} text={language.displayLanguage || language.locale || ''}>
                                            {language.displayLanguage}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '-8px', marginBottom: 16 }}>
                            <Text size={200} italic style={{ color: tokens.colorNeutralForeground2 }}>
                                For best results, select the language you mainly speak. If it&apos;s not listed, it may still be supported via auto-detection.
                            </Text>
                        </div>
                        <div className={classes.voiceSettingRow}>
                            <div className={classes.voiceSettingLeft}>
                                <Text size={400} weight="semibold">
                                    {t('settings.voice.entryType', 'Send Options')}
                                </Text>
                                <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                                    {t('settings.voice.entryTypeDescription', 'Choose how voice input is sent')}
                                </Text>
                            </div>
                            <div className={classes.voiceSettingRight}>
                                <Dropdown
                                    aria-labelledby="voice-entry-type-dropdown"
                                    placeholder="Select entry type"
                                    value={voiceEntryTypes.find((type: VoiceEntryType) => type.key === voiceEntryType)?.text}
                                    selectedOptions={[voiceEntryType]}
                                    onOptionSelect={(_, data) => {
                                        if (data.optionValue) {
                                            onUpdateVoiceEntryType(data.optionValue);
                                        }
                                    }}
                                >
                                    {voiceEntryTypes.map((type: VoiceEntryType) => (
                                        <Option key={type.key} value={type.key}>
                                            {type.text}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                        </div>
                        {voiceEntryType === 'Automatic Send' && (
                            <Text size={200} italic style={{ color: tokens.colorNeutralForeground2, marginTop: '-8px', paddingRight: '40px', width: '100%' }}>
                                {t('settings.voice.automaticSendDescription', 'You have a maximum of 30 seconds to record your voice message. The recording will be sent automatically at the end of a silence time period.')}
                            </Text>
                        )}
                        {voiceEntryType === 'Click Send' && (
                            <Text size={200} italic style={{ color: tokens.colorNeutralForeground2, marginTop: '-8px', paddingRight: '40px', width: '100%' }}>
                                {t('settings.voice.clickSendDescription', 'You have a maximum of 30 seconds to record your voice message. You can send, pause, cancel, or edit your recording in the prompt textbox before sending.')}
                            </Text>
                        )}

                        {voiceEntryType === 'Automatic Send' && (
                            <div className={classes.voiceSettingRow} style={{ marginTop: '16px' }}>
                                <div className={classes.voiceSettingLeft} style={{ marginRight: '20px' }}>
                                    <Text size={400} weight="semibold">
                                        {t('settings.voice.autoSendNumSecs', 'Number of Seconds until Auto-Send')}
                                    </Text>
                                    <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                                        {t('settings.voice.autoSendNumSecsDescription', 'How many seconds to wait before automatically sending the voice message')}
                                    </Text>
                                </div>
                                <div className={classes.voiceSettingRight}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Input
                                            className={classes.autoSendSecsInput}
                                            type="number"
                                            value={autoSendNumSecsInput}
                                            min={1}
                                            max={MAX_AUTO_SEND_SECONDS}
                                            style={{
                                                borderColor: parseInt(autoSendNumSecsInput) > MAX_AUTO_SEND_SECONDS ? tokens.colorPaletteRedBorder1 : undefined,
                                                borderWidth: parseInt(autoSendNumSecsInput) > MAX_AUTO_SEND_SECONDS ? '2px' : undefined
                                            }}
                                            onChange={(_, data) => {
                                                const value = data.value;
                                                setAutoSendNumSecsInput(value);

                                                if (validateAutoSendNumSecs(value)) {
                                                    const numValue = parseInt(value);
                                                    onUpdateAutoSendNumSecs(numValue);
                                                }
                                            }}
                                            aria-label="Number of seconds until auto-send"
                                        />
                                        {parseInt(autoSendNumSecsInput) > MAX_AUTO_SEND_SECONDS && (
                                            <Warning24Regular style={{ color: tokens.colorPaletteRedForeground1, flexShrink: 0 }} />
                                        )}
                                    </div>
                                    {(autoSendNumSecsError || parseInt(autoSendNumSecsInput) > MAX_AUTO_SEND_SECONDS) && (
                                        <Text className={classes.errorText} size={200} style={{ color: tokens.colorPaletteRedForeground1, marginTop: '4px' }}>
                                            {parseInt(autoSendNumSecsInput) > MAX_AUTO_SEND_SECONDS ? `Maximum value is ${MAX_AUTO_SEND_SECONDS} seconds` : autoSendNumSecsError}
                                        </Text>
                                    )}
                                </div>
                            </div>
                        )}
                        {/* Show/Hide Language Selection Toggle */}
                        <div className={classes.themeSettingContainer} style={isMobile ? { flexDirection: 'column', alignItems: 'flex-start', marginTop: '0px' } : { marginTop: '0px' }}>
                            <div className={classes.themeSettingLeft}>
                                <Text size={400} weight="semibold">
                                    {t('settings.languageSelection.title', 'Show/Hide Language Selection in Prompt Text')}
                                </Text>
                                <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                                    {t('settings.languageSelection.description', 'Select to show the language selection button in the prompt textbox of the conversations')}
                                </Text>
                            </div>
                            <div className={classes.themeSettingRight} style={isMobile ? { width: '100%', justifyContent: 'left', marginTop: '12px' } : {}}>
                                
                                <EyeOff24Regular className={showLanguageSelectionInPrompt ? classes.themeIconDark : classes.themeIconLight} />
                                <Switch
                                    className={!showLanguageSelectionInPrompt ? classes.themeSwitchLight : classes.themeSwitchDark}
                                    checked={showLanguageSelectionInPrompt}
                                    onChange={(_, data) => onUpdateShowLanguageSelectionInPrompt(data.checked)}
                                    aria-label={t('settings.languageSelection.toggle', 'Toggle show/hide language selection')}
                                />
                                <Eye24Regular className={!showLanguageSelectionInPrompt ? classes.themeIconLight : classes.themeIconDark} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* User Survey Section - Role-based visibility */}
                {surveyManagementEnabled && (
                    <div className={classes.settingsSection}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <Text className={classes.sectionTitle} size={500}>
                                {t('settings.survey.title', 'User Survey')}
                            </Text>
                            <Tooltip 
                                content={
                                    <div style={{ textAlign: 'center' }}>
                                        <div>View Survey Documentation</div>
                                        <Link 
                                            href="https://dev.azure.com/SYENSQO-SA/SyGPT/_wiki/wikis/Syensqo-AI.wiki/368/Survey" 
                                            target="_blank"
                                            style={{ fontSize: '12px', color: tokens.colorBrandForeground1 }}
                                        >
                                            📖 Open Documentation
                                        </Link>
                                    </div>
                                } 
                                relationship="description"
                            >
                                <Info16Regular 
                                    style={{ 
                                        color: tokens.colorNeutralForeground2, 
                                        cursor: 'pointer'
                                    }} 
                                />
                            </Tooltip>
                        </div>
                        <div className={classes.voiceSettingContainer}>
                            {/* Survey Activation Toggle */}
                            <div className={classes.themeSettingContainer} style={isMobile ? { flexDirection: 'column', alignItems: 'flex-start', marginTop: '0px' } : { marginTop: '0px' }}>
                                <div className={classes.themeSettingLeft}>
                                    <Text size={400} weight="semibold">
                                        {t('settings.survey.activation.title', 'Survey Status')}
                                    </Text>
                                    <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                                        {t('settings.survey.activation.description', 'Activate or deactivate the survey system for users')}
                                    </Text>
                                </div>
                                <div className={classes.themeSettingRight} style={isMobile ? { width: '100%', justifyContent: 'left', marginTop: '12px' } : {}}>
                                    <Text size={300} style={{ color: isSurveyActive ? tokens.colorNeutralForeground2 : tokens.colorBrandForeground1 }}>
                                        {t('settings.survey.inactive', 'Inactive')}
                                    </Text>
                                    <Switch
                                        className={isSurveyActive ? classes.themeSwitchDark : classes.themeSwitchLight}
                                        checked={isSurveyActive}
                                        onChange={(_, data) => onSurveyActivationChange(data.checked)}
                                        aria-label={t('settings.survey.activation.toggle', 'Toggle survey activation')}
                                    />
                                    <Text size={300} style={{ color: isSurveyActive ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground2 }}>
                                        {t('settings.survey.active', 'Active')}
                                    </Text>
                                </div>
                            </div>
                            
                            {/* Survey Upload - Always visible when survey management is enabled */}
                            <div className={classes.voiceSettingRow} style={isMobile ? { flexDirection: 'column', alignItems: 'flex-start', marginTop: '0px' } : { marginTop: '0px' }}>
                                <div className={classes.voiceSettingLeft}>
                                    <Text size={400} weight="semibold">
                                        {t('settings.survey.upload.title', 'Upload survey JSON')}
                                    </Text>
                                    <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                                        {t('settings.survey.upload.description', 'Upload a JSON file to create or update surveys in the system. Always put "id" as the more recent, for example "s_202507_2" for the second survey of July 2025.')}
                                    </Text>
                                </div>
                                    <div className={classes.voiceSettingRight} style={isMobile ? { width: '100%', justifyContent: 'left', marginTop: '12px' } : {}}>
                                        <input
                                            type="file"
                                            accept=".json"
                                            style={{ display: 'none' }}
                                            id="survey-file-upload"
                                            onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    await onFileUpload(file);
                                                    // Reset input
                                                    e.target.value = '';
                                                }
                                            }}
                                        />
                                        <Button
                                            appearance="secondary"
                                            icon={<ArrowUpload24Regular />}
                                            disabled={isUploading}
                                            onClick={() => {
                                                document.getElementById('survey-file-upload')?.click();
                                            }}
                                            style={{
                                                backgroundColor: tokens.colorBrandBackground,
                                                color: tokens.colorNeutralForegroundOnBrand,
                                                border: `1px solid ${tokens.colorBrandBackground}`,
                                            }}
                                        >
                                            {isUploading ? 
                                                t('settings.survey.upload.uploading', 'Uploading...') :
                                                t('settings.survey.upload.button', 'Upload survey')
                                            }
                                        </Button>
                                    </div>
                                </div>

                            {/* Editor Type Toggle - Only show when survey is inactive */}
                            {!isSurveyActive && currentSurvey && (
                                <div className={classes.themeSettingContainer} style={isMobile ? { flexDirection: 'column', alignItems: 'flex-start', marginTop: '16px' } : { marginTop: '16px' }}>
                                    <div className={classes.themeSettingLeft}>
                                        <Text size={400} weight="semibold">
                                            {t('settings.survey.editor.title', 'Editor Type')}
                                        </Text>
                                        <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                                            {t('settings.survey.editor.description', 'Choose between visual form builder or JSON code editor')}
                                        </Text>
                                    </div>
                                    <div className={classes.themeSettingRight} style={isMobile ? { width: '100%', justifyContent: 'left', marginTop: '12px' } : {}}>
                                        <Code24Regular className={(showVisualEditor ?? localShowVisualEditor) ? classes.themeIconDark : classes.themeIconLight} />
                                        <Switch
                                            className={(showVisualEditor ?? localShowVisualEditor) ? classes.themeSwitchDark : classes.themeSwitchLight}
                                            checked={showVisualEditor ?? localShowVisualEditor}
                                            onChange={(_, data) => {
                                                if (onToggleEditor) {
                                                    onToggleEditor();
                                                } else {
                                                    setLocalShowVisualEditor(data.checked);
                                                }
                                            }}
                                            aria-label={t('settings.survey.editor.toggle', 'Toggle between JSON and Visual editor')}
                                        />
                                        <DesignIdeas24Regular className={(showVisualEditor ?? localShowVisualEditor) ? classes.themeIconLight : classes.themeIconDark} />
                                    </div>
                                </div>
                            )}

                            {/* Current Survey Display (active or most recent) */}
                            <div className={classes.voiceSettingRow} style={isMobile ? { flexDirection: 'column', alignItems: 'flex-start', marginTop: '16px' } : { marginTop: '16px' }}>
                                <div className={classes.voiceSettingLeft}>
                                    <Text size={400} weight="semibold">
                                        {isSurveyActive
                                            ? t('settings.survey.display.activeTitle', 'Active survey data')
                                            : t('settings.survey.display.mostRecentTitle', 'Most recent survey data')}
                                    </Text>
                                </div>
                                <div className={classes.voiceSettingRight} style={isMobile ? { width: '100%', justifyContent: 'left', marginTop: '12px' } : {}}>
                                    {isLoadingSurvey ? (
                                        <Text>Loading survey data...</Text>
                                    ) : currentSurvey ? (
                                        <div style={{ width: '100%' }}>
                                            {currentSurvey.id && (
                                                <Text size={300} style={{ marginBottom: '8px', color: tokens.colorBrandForeground1 }}>
                                                    {isSurveyActive
                                                        ? t('settings.survey.display.activeFound', 'Active survey found ID:')
                                                        : t('settings.survey.display.mostRecentFound', 'Most recent survey found ID:')}{' '}
                                                    <Text weight="bold">{currentSurvey.id}</Text>
                                                </Text>
                                            )}
                                            
                            {/* Show editable interface only when inactive, read-only display when active */}
                            {!isSurveyActive ? (
                                // Editable mode (inactive survey)
                                <>
                                    {(showVisualEditor ?? localShowVisualEditor) ? (
                                        // Visual Editor Mode
                                        <div style={{ width: '100%', marginBottom: '12px' }}>
                                            <VisualSurveyEditor
                                                initialSurvey={currentSurvey}
                                                onSave={async (survey) => {
                                                    // Convert survey object to JSON string
                                                    const jsonString = JSON.stringify(survey, null, 2);
                                                    
                                                    // Update the edited survey JSON state for consistency
                                                    setEditedSurveyJson(jsonString);
                                                    
                                                    // Use direct save if available, otherwise fallback to regular save with delay
                                                    if (onSaveSurveyDirect) {
                                                        await onSaveSurveyDirect(jsonString);
                                                    } else {
                                                        // Small delay to allow state update, then call regular save
                                                        await new Promise(resolve => setTimeout(resolve, 100));
                                                        await onSaveSurvey();
                                                    }
                                                }}
                                                onCancel={() => {
                                                    if (onToggleEditor) {
                                                        onToggleEditor();
                                                    } else {
                                                        setLocalShowVisualEditor(false);
                                                    }
                                                }}
                                                isSaving={isSaving}
                                                className=""
                                            />
                                        </div>
                                    ) : (
                                        // JSON Editor Mode
                                        <>
                                            <div style={{ width: '100%', marginBottom: '8px', maxWidth: '600px' }}>
                                                {/* Contenteditable div with ref to avoid cursor jump */}
                                                <ContentEditableSurvey
                                                    value={editedSurveyJson}
                                                    onChange={setEditedSurveyJson}
                                                    style={{
                                                        backgroundColor: tokens.colorNeutralBackground2,
                                                        border: `1px solid ${tokens.colorNeutralStroke1}`,
                                                        borderRadius: '4px',
                                                        padding: '12px',
                                                        height: '400px',
                                                        width: '100%',
                                                        overflow: 'auto',
                                                        fontFamily: 'monospace',
                                                        fontSize: '12px',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        boxSizing: 'border-box',
                                                        outline: 'none',
                                                    }}
                                                />
                                            </div>
                                            <div style={{ width: '100%', maxWidth: '600px', display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                                                <Button
                                                    appearance="primary"
                                                    icon={<Save24Regular />}
                                                    onClick={onSaveSurvey}
                                                    disabled={isSaving || !editedSurveyJson.trim()}
                                                    style={{
                                                        backgroundColor: tokens.colorBrandBackground,
                                                        color: tokens.colorNeutralForegroundOnBrand,
                                                    }}
                                                >
                                                    {isSaving 
                                                        ? t('settings.survey.save.saving', 'Saving...') 
                                                        : t('settings.survey.save.button', 'Save Survey')
                                                    }
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : (
                                // Read-only mode (active survey)
                                <div style={{
                                    backgroundColor: tokens.colorNeutralBackground2,
                                    border: `1px solid ${tokens.colorNeutralStroke1}`,
                                    borderRadius: '4px',
                                    padding: '12px',
                                    height: '400px',
                                    maxWidth: '600px',
                                    overflow: 'auto',
                                    fontFamily: 'monospace',
                                    fontSize: '12px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    marginBottom: '12px',
                                    boxSizing: 'border-box'
                                }}>
                                    {JSON.stringify(currentSurvey, null, 2)}
                                </div>
                            )}                                            {/* Save button moved above, only shown when inactive */}
                                            
                                            {/* Show info text when active */}
                                            {isSurveyActive && (
                                                <Text size={200} italic style={{ color: tokens.colorNeutralForeground2, marginTop: '8px' }}>
                                                    {t('settings.survey.edit.activeInfo', 'Survey is active and read-only. Deactivate to edit.')}
                                                </Text>
                                            )}
                                        </div>
                                    ) : (
                                        <div>
                                            <Text style={{ color: tokens.colorNeutralForeground2 }}>{t('settings.survey.display.none', 'No survey data found')}</Text>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Conversations Section */}
                <div className={classes.settingsSection}>
                    <Text className={classes.sectionTitle} size={500}>
                        {t('settings.operations.title', 'Conversations')}
                    </Text>
                    <div className={classes.buttonsContainer} style={{ marginTop: 0 }}>
                        <div>
                            <Button
                                className={classes.actionButton}
                                icon={<BinRecycle24Regular />}
                                onClick={handleClearConversations}
                            >
                                {t('menu.persona.clearConversations', 'Clear conversations')}
                            </Button>
                            <Text size={300} italic style={{ color: tokens.colorNeutralForeground2, marginTop: tokens.spacingVerticalS }}>
                                {t('settings.dataManagement.clearConversationsDescription', 'This will permanently delete all your conversation history')}
                            </Text>
                        </div>
                    </div>
                </div>

                {/* Account Section */}
                <div className={classes.settingsSection}>
                    <Text className={classes.sectionTitle} size={500}>
                        {t('settings.sygptLogout.title', 'SyGPT Logout')}
                    </Text>

                    <div className={classes.buttonsContainer} style={{ marginTop: 0 }}>
                        <div>
                            <Button
                                className={classes.dangerActionButton}
                                icon={<SignOut24Regular />}
                                onClick={handleLogout}
                            >
                                {t('settings.logout.button', 'Log out')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsView;
